import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaRoad, 
  FaArchway, 
  FaTrain, 
  FaIndustry, 
  FaMountain, 
  FaClipboardCheck, 
  FaBuilding, 
  FaLeaf,
  FaCheckCircle
} from 'react-icons/fa';

const BusinessScopeSection: React.FC<{ language: 'ar' | 'en' }> = ({ language }) => {
  const isRTL = language === 'ar';
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const businessAreas = [
    {
      id: 1,
      icon: FaRoad,
      titleAr: 'مشروعات الطرق',
      titleEn: 'Road Projects',
      descriptionAr: 'تنفيذ وتطوير شبكات الطرق السريعة والرئيسية بمواصفات عالمية لتحقيق كفاءة وسلامة التنقل.',
      descriptionEn: 'Implementation and development of main and highway road networks according to international standards for efficient and safe transportation.',
      featuresAr: [
        'تنفيذ مشروعات قومية ضخمة',
        'الاعتماد على تقنيات حديثة',
        'تسليم المشاريع في المواعيد المحددة',
        'تطبيق معايير السلامة المرورية',
        'فرق عمل ذات خبرة عالية'
      ],
      featuresEn: [
        'Execution of major national projects',
        'Utilizing advanced technologies',
        'On-time project delivery',
        'Applying top safety standards',
        'Highly experienced teams'
      ],
      color: 'from-blue-600 to-blue-700',
      bgColor: 'bg-blue-50',
      image: 'https://images.unsplash.com/photo-1545454675-3531b543be5d?auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 2,
      icon: FaArchway,
      titleAr: 'مشروعات الجسور',
      titleEn: 'Bridge Projects',
      descriptionAr: 'تصميم وتنفيذ الجسور والكباري وفق أفضل الممارسات الهندسية لضمان القوة والمتانة.',
      descriptionEn: 'Design and construction of bridges using best engineering practices to ensure strength and durability.',
      featuresAr: [
        'حلول هندسية متكاملة',
        'استخدام مواد بناء متطورة',
        'خبرة في المشاريع القومية',
        'فحص واختبار الجودة بشكل مستمر',
        'التزام بأعلى درجات الأمان'
      ],
      featuresEn: [
        'Integrated engineering solutions',
        'Use of advanced construction materials',
        'Expertise in national projects',
        'Continuous quality testing',
        'Commitment to top safety'
      ],
      color: 'from-purple-600 to-purple-700',
      bgColor: 'bg-purple-50',
      image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 3,
      icon: FaTrain,
      titleAr: 'خطوط السكك الحديدية والقطار السريع',
      titleEn: 'Railways & High-Speed Trains',
      descriptionAr: 'تنفيذ وصيانة خطوط السكك الحديدية والمساهمة في تطوير القطار السريع بأعلى مستويات الكفاءة.',
      descriptionEn: 'Implementation and maintenance of railways, contributing to high-speed train development with maximum efficiency.',
      featuresAr: [
        'خبرة في مشروعات كبرى',
        'تكنولوجيا متقدمة في التنفيذ',
        'تطبيق أنظمة أمان صارمة',
        'حلول صديقة للبيئة'
      ],
      featuresEn: [
        'Experience in large-scale projects',
        'Advanced technologies in execution',
        'Strict safety systems',
        'Eco-friendly solutions'
      ],
      color: 'from-green-600 to-green-700',
      bgColor: 'bg-green-50',
      image: 'https://images.unsplash.com/photo-1518837695005-2083093ee35b?auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 4,
      icon: FaIndustry,
      titleAr: 'أعمال البنية التحتية والمرافق',
      titleEn: 'Infrastructure & Utilities',
      descriptionAr: 'تطوير وتنفيذ مشاريع البنية التحتية والمرافق الحيوية للمجتمعات والمدن.',
      descriptionEn: 'Development and execution of vital infrastructure and utility projects for communities and cities.',
      featuresAr: [
        'تكامل الخدمات',
        'التزام بمعايير الجودة',
        'سرعة استجابة للطوارئ',
        'إدارة فعّالة للمشروعات'
      ],
      featuresEn: [
        'Integrated services',
        'Quality standards commitment',
        'Fast emergency response',
        'Efficient project management'
      ],
      color: 'from-orange-600 to-orange-700',
      bgColor: 'bg-orange-50',
      image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 5,
      icon: FaMountain,
      titleAr: 'إدارة وتشغيل المحاجر',
      titleEn: 'Quarry Management',
      descriptionAr: 'استخراج ومعالجة المواد الخام من المحاجر لتلبية متطلبات البناء والصناعة.',
      descriptionEn: 'Extraction and processing of raw materials from quarries to meet construction and industrial needs.',
      featuresAr: [
        'معدات متطورة لاستخراج الخامات',
        'جودة المواد المنتجة',
        'التزام بالاستدامة البيئية',
        'سرعة التوريد'
      ],
      featuresEn: [
        'Advanced extraction equipment',
        'High-quality output',
        'Environmental sustainability',
        'Fast supply chain'
      ],
      color: 'from-red-600 to-red-700',
      bgColor: 'bg-red-50',
      image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 6,
      icon: FaClipboardCheck,
      titleAr: 'إدارة المشاريع والتحكم في الجودة والتكلفة',
      titleEn: 'Project Management & Quality Control',
      descriptionAr: 'تخطيط وتنفيذ ومتابعة المشاريع لضمان الالتزام بالجودة والوقت والتكلفة.',
      descriptionEn: 'Planning, executing, and monitoring projects to ensure quality, time, and cost control.',
      featuresAr: [
        'فريق هندسي محترف',
        'تطبيق أنظمة متابعة رقمية',
        'تقارير دقيقة للمشاريع',
        'تحسين مستمر في الأداء'
      ],
      featuresEn: [
        'Professional engineering team',
        'Digital monitoring systems',
        'Accurate project reports',
        'Continuous performance improvement'
      ],
      color: 'from-indigo-600 to-indigo-700',
      bgColor: 'bg-indigo-50',
      image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 7,
      icon: FaBuilding,
      titleAr: 'التوسعات العمرانية والمجتمعية',
      titleEn: 'Urban & Community Expansions',
      descriptionAr: 'تنفيذ مشروعات الإسكان والبنية العمرانية الحديثة للمجتمعات الجديدة.',
      descriptionEn: 'Implementation of housing and modern urban development projects for new communities.',
      featuresAr: [
        'حلول تصميم مبتكرة',
        'تلبية الاحتياجات السكانية',
        'التزام بالاستدامة',
        'تنسيق عمراني متكامل'
      ],
      featuresEn: [
        'Innovative design solutions',
        'Meeting population needs',
        'Sustainability commitment',
        'Integrated urban planning'
      ],
      color: 'from-teal-600 to-teal-700',
      bgColor: 'bg-teal-50',
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 8,
      icon: FaLeaf,
      titleAr: 'المشروعات البيئية والاستدامة',
      titleEn: 'Environmental & Sustainability Projects',
      descriptionAr: 'تبني معايير الاستدامة وحماية البيئة في جميع مراحل المشاريع.',
      descriptionEn: 'Adopting sustainability and environmental protection standards in all project stages.',
      featuresAr: [
        'استخدام تقنيات صديقة للبيئة',
        'إدارة المخلفات بشكل آمن',
        'حلول خضراء في الإنشاءات',
        'نشر الوعي البيئي'
      ],
      featuresEn: [
        'Use of eco-friendly technologies',
        'Safe waste management',
        'Green construction solutions',
        'Environmental awareness promotion'
      ],
      color: 'from-emerald-600 to-emerald-700',
      bgColor: 'bg-emerald-50',
      image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=800&q=80'
    }
  ];

  return (
    <section className={`section-padding bg-gray-50 ${isRTL ? 'font-cairo' : 'font-montserrat'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-block px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4"
          >
            {language === 'ar' ? 'مجالات أعمالنا' : 'Scope of Our Business'}
          </motion.span>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
          >
            {language === 'ar' ? 'مجالات أعمالنا' : 'Scope of Our Business'}
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-lg text-gray-600 max-w-4xl mx-auto"
          >
            {language === 'ar'
              ? 'شركة مكة البركة للمقاولات تقدم مجموعة متكاملة من الخدمات الهندسية والإنشائية، تجمع بين الجودة، والدقة، والكفاءة في تنفيذ المشاريع العملاقة بمختلف القطاعات، مع الالتزام بأعلى معايير الوقت والتكلفة.'
              : 'Mecca El Baraka for Construction offers a comprehensive range of engineering and construction services, combining quality, precision, and efficiency in delivering mega projects across multiple sectors, with a commitment to the highest standards of time and cost control.'}
          </motion.p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="relative">
          {/* Sticky Triangle Image on the Left */}
          <div className="hidden lg:block lg:sticky lg:top-8 lg:float-left lg:w-1/3 lg:pr-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg border border-gray-100">
                <div className="relative w-full h-80 lg:h-[450px] flex items-center justify-center">
                  {/* Project Management Triangle Image */}
                  <img
                    src="/images/image55.png"
                    alt={language === 'ar' ? 'مثلث إدارة المشاريع' : 'Project Management Triangle'}
                    className="w-full h-full object-contain rounded-lg bg-gray-50"
                    style={{ minHeight: '300px', maxHeight: '400px' }}
                  />
                </div>
                
                {/* Diagram Description */}
                <div className="mt-6 text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {language === 'ar' ? 'مثلث إدارة المشاريع' : 'Project Management Triangle'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {language === 'ar' 
                      ? 'نلتزم بالتوازن المثالي بين النطاق والتكلفة والوقت مع الحفاظ على أعلى معايير الجودة'
                      : 'We commit to the perfect balance between scope, cost, and time while maintaining the highest quality standards'}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Mobile Triangle Image */}
          <div className="lg:hidden mb-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg border border-gray-100">
                <div className="relative w-full h-80 flex items-center justify-center">
                  {/* Project Management Triangle Image */}
                  <img
                    src="/images/image55.png"
                    alt={language === 'ar' ? 'مثلث إدارة المشاريع' : 'Project Management Triangle'}
                    className="w-full h-full object-contain rounded-lg bg-gray-50"
                    style={{ minHeight: '300px', maxHeight: '400px' }}
                  />
                </div>
                
                {/* Diagram Description */}
                <div className="mt-6 text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {language === 'ar' ? 'مثلث إدارة المشاريع' : 'Project Management Triangle'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {language === 'ar' 
                      ? 'نلتزم بالتوازن المثالي بين النطاق والتكلفة والوقت مع الحفاظ على أعلى معايير الجودة'
                      : 'We commit to the perfect balance between scope, cost, and time while maintaining the highest quality standards'}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Business Areas Cards - Float Right */}
          <div className="lg:float-right lg:w-2/3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {businessAreas.map((area, index) => (
                <motion.div
                  key={area.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  onMouseEnter={() => setHoveredCard(area.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  className="group cursor-pointer"
                >
                  <div className={`bg-white rounded-2xl p-6 shadow-lg border border-gray-100 transition-all duration-300 h-full ${
                    hoveredCard === area.id ? 'transform -translate-y-2 shadow-xl' : 'hover:shadow-lg'
                  }`}>
                    {/* Card Header */}
                    <div className="flex items-start space-x-4 rtl:space-x-reverse mb-4">
                      <div className={`w-12 h-12 bg-gradient-to-r ${area.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <area.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-2">
                          {isRTL ? area.titleAr : area.titleEn}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-3">
                          {isRTL ? area.descriptionAr : area.descriptionEn}
                        </p>
                      </div>
                    </div>

                    {/* Features List */}
                    <div className="space-y-2">
                      {(isRTL ? area.featuresAr : area.featuresEn).map((feature, featureIndex) => (
                        <motion.div
                          key={featureIndex}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: (index * 0.1) + (featureIndex * 0.05) }}
                          className="flex items-center space-x-2 rtl:space-x-reverse"
                        >
                          <FaCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </motion.div>
                      ))}
                    </div>

                    {/* Hover Effect Overlay */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${area.color} rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Clearfix for float layout */}
          <div className="clear-both"></div>
        </div>
      </div>
    </section>
  );
};

export default BusinessScopeSection; 